
import { useEffect, useState } from "react";
import { Award, Search, ExternalLink, Loader2, FileText } from "lucide-react";
import { supabase } from "@/lib/supabase";

export default function CertificationsList() {
    const [certs, setCerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        const loadCertifications = async () => {
            const { data, error } = await supabase
                .from("certifications")
                .select("*")
                .order("issue_date", { ascending: false });

            if (error) {
                console.error("Certifications loading error:", error);
                setError("Unable to load certifications right now.");
            } else {
                setCerts(data || []);
            }

            setLoading(false);
        };

        loadCertifications();
    }, []);

    const filteredCerts = certs.filter((cert) =>
        [
            cert.title,
            cert.issuer,
            cert.credential_id,
            cert.description,
        ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            .includes(search.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex items-center gap-3 text-muted2 py-10">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className="mono text-xs">LOADING CERTIFICATIONS...</span>
            </div>
        );
    }

    if (error) {
        return <p className="text-sm text-muted2 py-8">{error}</p>;
    }

    return (
        <div data-testid="certifications-list">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                <p className="mono text-xs tracking-widest text-muted2">
                    {certs.length} CERTIFICATE{certs.length === 1 ? "" : "S"}
                </p>

                <div className="relative w-full sm:w-72">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted2" />
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search certificates..."
                        className="w-full border border-line bg-paper pl-10 pr-4 py-3 text-sm"
                    />
                </div>
            </div>

            {filteredCerts.length === 0 ? (
                <div className="border-t border-line py-10">
                    <Award className="h-6 w-6 text-muted2 mb-3" />
                    <p className="text-sm text-muted2">
                        {certs.length === 0
                            ? "No certifications added yet."
                            : "No certificates match your search."}
                    </p>
                </div>
            ) : (
                <div className="grid md:grid-cols-2 gap-5">
                    {filteredCerts.map((cert) => {
                        const fileUrl = supabase.storage
                            .from("certifications")
                            .getPublicUrl(cert.file_path).data.publicUrl;

                        const isImage = cert.file_type?.startsWith("image/");

                        return (
                            <article
                                key={cert.id}
                                className="border border-line bg-paper p-5"
                            >
                                <div className="flex items-start gap-3">
                                    <Award className="h-5 w-5 mt-1 text-sage shrink-0" />
                                    <div className="min-w-0">
                                        <h2 className="font-display text-xl leading-tight">
                                            {cert.title}
                                        </h2>
                                        <p className="text-sm text-muted2 mt-2">
                                            {cert.issuer}
                                        </p>
                                    </div>
                                </div>

                                {isImage ? (
                                    <a
                                        href={fileUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="block mt-5"
                                    >
                                        <img
                                            src={fileUrl}
                                            alt={`Certificate: ${cert.title}`}
                                            className="w-full max-h-64 object-contain border border-line bg-paper2"
                                            loading="lazy"
                                        />
                                    </a>
                                ) : (
                                    <div className="mt-5 border border-line bg-paper2 p-5 flex items-center gap-3">
                                        <FileText className="h-8 w-8 text-muted2" />
                                        <span className="mono text-xs">PDF CERTIFICATE</span>
                                    </div>
                                )}

                                {cert.description && (
                                    <p className="text-sm leading-6 text-muted2 mt-4">
                                        {cert.description}
                                    </p>
                                )}

                                <div className="mt-4 space-y-2">
                                    {cert.issue_date && (
                                        <p className="mono text-xs text-muted2">
                                            ISSUED: {new Date(cert.issue_date + "T00:00:00").toLocaleDateString("en-IN", {
                                                month: "long",
                                                year: "numeric",
                                            })}
                                        </p>
                                    )}

                                    {cert.credential_id && (
                                        <p className="mono text-xs text-muted2 break-all">
                                            CREDENTIAL ID: {cert.credential_id}
                                        </p>
                                    )}
                                </div>

                                <div className="flex flex-wrap gap-4 mt-5 pt-4 border-t border-line">
                                    <a
                                        href={fileUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-2 mono text-xs underline underline-offset-4"
                                    >
                                        VIEW CERTIFICATE
                                        <ExternalLink className="h-3 w-3" />
                                    </a>

                                    {cert.credential_url && (
                                        <a
                                            href={cert.credential_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="inline-flex items-center gap-2 mono text-xs underline underline-offset-4"
                                        >
                                            VERIFY CREDENTIAL
                                            <ExternalLink className="h-3 w-3" />
                                        </a>
                                    )}
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
